import React, { useEffect, useRef, useState } from 'react';
import { submitEmailOnly, verifyPreLanderOtp, APIResponse } from '../../utils/api';

const OTP_LENGTH = 5;
const RESEND_COOLDOWN_FALLBACK = 60;

const getResendSeconds = (otpExpiry?: string): number => {
  if (!otpExpiry) return RESEND_COOLDOWN_FALLBACK;
  const ms = new Date(otpExpiry).getTime() - Date.now();
  return ms > 0 ? Math.ceil(ms / 1000) : 0;
};

interface OtpStepProps {
  email: string;
  initialOtpExpiry?: string;
  onBack: () => void;
  onVerified: (data: APIResponse) => void;
  ab_testing?: Record<string, unknown>;
}

const OtpStep: React.FC<OtpStepProps> = ({
  email,
  initialOtpExpiry,
  onBack,
  onVerified,
  ab_testing,
}) => {
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [resendIn, setResendIn] = useState<number>(() => getResendSeconds(initialOtpExpiry));
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => {
      setResendIn((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    setOtp((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    if (error) setError('');
    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    setOtp(() => {
      const next = Array(OTP_LENGTH).fill('');
      for (let i = 0; i < OTP_LENGTH; i += 1) {
        next[i] = pasted[i] || '';
      }
      return next;
    });
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
    if (error) setError('');
  };

  const handleVerify = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const code = otp.join('');
    if (code.length !== OTP_LENGTH) {
      setError('Please enter the 5-digit code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await verifyPreLanderOtp(email, code);
      if (data?.success === false) {
        setError(data.message || 'Please check the code and try again');
        return;
      }
      if (data?.authToken || data?.token) {
        onVerified(data);
        return;
      }
      setError(data?.message || 'Please check the code and try again');
    } catch (err: any) {
      setError(err?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || resending) return;
    setResending(true);
    setError('');
    try {
      const data = await submitEmailOnly(email, { isResend: true, ab_testing });
      if (data?.success === false) {
        setError(data.message || 'Network error. Please try again.');
        return;
      }
      if (data?.redirectUrl || data?.loginUrl) {
        onVerified(data);
        return;
      }
      setResendIn(getResendSeconds(data?.otpExpiry) || RESEND_COOLDOWN_FALLBACK);
    } catch (err: any) {
      setError(err?.message || 'Network error. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="w-full">
      <div className="w-full">
        <div className="text-center mb-[30px]">
          <h2 className="text-white text-center font-poppins text-[28px] font-semibold leading-[120%] capitalize mb-[10px]">
            Verify Code
          </h2>
          <p className="text-white/75 font-poppins text-sm font-medium leading-[140%]">
            Enter the code we sent to
          </p>
          <p className="text-white font-poppins text-sm font-semibold leading-[140%] mt-1 break-all">
            {email}
          </p>
        </div>

        <form onSubmit={handleVerify} className="w-full max-w-[330px] mx-auto">
          <div className="flex items-start gap-2 w-full pb-7">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputsRef.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                aria-label={`Code digit ${index + 1}`}
                className="flex-1 min-w-0 h-[74px] px-3 text-center rounded-lg border border-white/10 bg-white/10 text-[#d9d1dc] font-poppins text-[28px] font-semibold leading-[140%] tracking-[-0.56px] focus:outline-none focus:border-[#fb6284]"
              />
            ))}
          </div>

          {error && (
            <div className="text-[#ff4444] font-poppins text-sm font-normal leading-[120%] text-start mb-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="rounded-[18px] bg-gradient-to-r from-[#8350cb] to-[#fe6283] shadow-[0_4px_16.8px_0_rgba(240,73,243,0.47)] border-none px-[15px] py-[15px] text-white text-base font-semibold cursor-pointer transition-all duration-300 w-full mb-5 hover:bg-[#2a2a2a] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? 'Verifying...' : 'Apply Code'}
          </button>

          <div className="text-center mb-3">
            <button
              type="button"
              onClick={handleResend}
              disabled={resendIn > 0 || resending}
              className="text-[#e0dff5] font-poppins bg-transparent text-xs font-medium leading-[120%] border-none cursor-pointer transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {resendIn > 0
                ? `Resend code (${resendIn}s)`
                : resending
                  ? 'Sending...'
                  : 'Resend code'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={onBack}
              className="text-[#8b8f9c] font-poppins bg-transparent text-xs font-medium leading-[120%] border-none cursor-pointer transition-colors duration-300"
            >
              ← Back
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OtpStep;
